import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';
import API_BASE_URL, { BASE_URL } from '../config';

const ImageContainer = styled(Box)(({ theme }) => ({
  height: 250,
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 0.3s ease-in-out',
  },
  '&:hover img': {
    transform: 'scale(1.05)',
  },
}));

const Photos = React.memo(({ photos = [], fetchPhotos }) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibilityMap, setVisibilityMap] = useState({});
  const [selectedYear, setSelectedYear] = useState('All');

  // Extract unique years from photos prop
  const availableYears = React.useMemo(() => {
    const years = new Set();
    photos.forEach(photo => {
      const year = photo.year || (photo.createdAt ? new Date(photo.createdAt).getFullYear() : 'Unknown');
      if (year && year !== 'Unknown') years.add(String(year));
    });
    // Sort descending (latest first)
    return Array.from(years).sort((a, b) => b - a);
  }, [photos]);

  // Optimized frontend filtering
  const filteredPhotos = React.useMemo(() => {
    if (selectedYear === 'All') return photos;
    return photos.filter(photo => {
      const year = photo.year || (photo.createdAt ? new Date(photo.createdAt).getFullYear() : 'Unknown');
      return String(year) === selectedYear;
    });
  }, [photos, selectedYear]);

  // Sync visibilityMap when photos prop changes
  React.useEffect(() => {
    const map = {};
    photos.forEach(p => { map[p.id] = p.status === 1; });
    setVisibilityMap(map);
  }, [photos]);

  const handleVisibilityToggle = async (e, photoId) => {
    e.stopPropagation();
    const newVal = !visibilityMap[photoId];
    setVisibilityMap(prev => ({ ...prev, [photoId]: newVal }));
    try {
      await axios.patch(`${API_BASE_URL}/photos/${photoId}`, { status: newVal ? 1 : 0 });
    } catch (err) {
      console.error('Visibility toggle error:', err);
      setVisibilityMap(prev => ({ ...prev, [photoId]: !newVal }));
    }
  };

  const openMenu = Boolean(anchorEl);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('blob:')) return path;
    if (path.startsWith('http') && !path.includes('localhost')) return path;
    if (path.startsWith('http') && path.includes('localhost')) {
      return path.replace(/http:\/\/localhost:\d+/, BASE_URL);
    }
    if (path.startsWith('/')) return `${BASE_URL}${path}`;
    return `${BASE_URL}/uploads/${path}`;
  };

  const handleOpen = (index) => {
    setSelectedIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedIndex(null);
  };

  const handleNext = () => {
    setSelectedIndex((prev) => prev < filteredPhotos.length - 1 ? prev + 1 : prev);
  };

  const handlePrevious = () => {
    setSelectedIndex((prev) => prev > 0 ? prev - 1 : prev);
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleDelete = async () => {
    try {
      const photoId = filteredPhotos[selectedIndex]?.id;
      if (!photoId) return;
      await axios.delete(`${API_BASE_URL}/photos/${photoId}`);
      await fetchPhotos();
      handleClose();
      handleMenuClose();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleToggleVisibility = async () => {
    try {
      const photo = filteredPhotos[selectedIndex];
      if (!photo) return;
      await axios.patch(`${API_BASE_URL}/photos/${photo.id}`, { status: photo.status === 1 ? 0 : 1 });
      await fetchPhotos();
      handleMenuClose();
    } catch (err) {
      console.error('Visibility error:', err);
    }
  };

  return (
    <>
      {/* Year Filter Dropdown */}
      {photos.length > 0 && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="year-filter-label">Filter by Year</InputLabel>
            <Select
              labelId="year-filter-label"
              value={selectedYear}
              label="Filter by Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="All">All Years</MenuItem>
              {availableYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Photo grid — grouped by year + place for timeline */}
      {filteredPhotos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            {photos.length === 0 ? 'No photos available' : 'No images found for the selected year'}
          </Typography>
        </Box>
      ) : (
        (() => {
          // Group filtered photos by year + place
          const groups = filteredPhotos.reduce((acc, photo) => {
            const year = photo.year || (photo.createdAt ? new Date(photo.createdAt).getFullYear() : 'Unknown');
            const place = photo.place || '';
            const key = place ? `${year} – ${place}` : `${year}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(photo);
            return acc;
          }, {});

          return Object.entries(groups).map(([groupKey, groupPhotos]) => (
            <Box key={groupKey} sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}
              >
                📅 {groupKey}
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(4,1fr)' },
                gap: 3
              }}>
                {groupPhotos.map((photo) => {
                  const globalIndex = filteredPhotos.findIndex(p => p.id === photo.id);
                  return (
                    <Box
                      key={photo.id}
                      onClick={() => handleOpen(globalIndex)}
                      sx={{ cursor: 'pointer', position: 'relative', borderRadius: 2 }}
                    >
                      <ImageContainer>
                        <img
                          src={getImageUrl(photo.filename)}
                          alt=""
                          onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.style.display = 'none'; }}
                          loading="lazy" // Native browser lazy loading
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {/* Visibility checkbox overlay — top-right corner */}
                        <Box
                          onClick={(e) => handleVisibilityToggle(e, photo.id)}
                          title={visibilityMap[photo.id] ? 'Public – click to make Private' : 'Private – click to make Public'}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 10,
                            width: 22,
                            height: 22,
                            borderRadius: '4px',
                            border: '2px solid white',
                            backgroundColor: visibilityMap[photo.id] ? '#4caf50' : 'rgba(0,0,0,0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                            transition: 'background-color 0.2s',
                          }}
                        >
                          {visibilityMap[photo.id] && (
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                              <polyline points="2,7 5,10 11,3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </Box>
                      </ImageContainer>
                      <p style={{ textAlign: 'center', marginTop: '8px', fontWeight: 'bold', height: 20 }}>
                        {visibilityMap[photo.id] ? 'Public' : 'Private'}
                      </p>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ));
        })()
      )}

      {/* Photo viewer dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        disableScrollLock
        keepMounted
        sx={{ backdropFilter: 'blur(8px)' }}
      >
        <DialogTitle>
          {filteredPhotos[selectedIndex]?.altText || 'Photo'}
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 10 }}>
            <CloseIcon />
          </IconButton>
          <IconButton onClick={handleMenuClick} sx={{ position: 'absolute', right: 50 }}>
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose} disableScrollLock>
            <MenuItem onClick={handleDelete}>Delete Photo</MenuItem>
            <MenuItem onClick={handleToggleVisibility}>
              {filteredPhotos[selectedIndex]?.status === 1 ? 'Make Private' : 'Make Public'}
            </MenuItem>
          </Menu>
        </DialogTitle>
        <DialogContent>
          {filteredPhotos[selectedIndex] && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', position: 'relative' }}>
              <IconButton onClick={handlePrevious} disabled={selectedIndex === 0} sx={{ position: 'absolute', left: 10 }}>
                <ArrowBackIosIcon />
              </IconButton>
              <img
                src={getImageUrl(filteredPhotos[selectedIndex].filename)}
                alt={filteredPhotos[selectedIndex]?.altText || ''}
                loading="lazy"
                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                onError={(e) => { e.target.onerror = null; e.target.src = ''; }}
              />
              <IconButton onClick={handleNext} disabled={selectedIndex === filteredPhotos.length - 1} sx={{ position: 'absolute', right: 10 }}>
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

export default Photos;
